import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  CheckCircle,
  Cancel,
  PersonAdd,
  PersonRemove,
  SportsScore,
  Visibility,
} from '@mui/icons-material';
import { GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMatchStore } from '../../stores/matchStore';
import { DataTable } from '../../components/admin/DataTable';
import { FormDialog } from '../../components/admin/FormDialog';
import { ConfirmDialog } from '../../components/admin/ConfirmDialog';
import { StatusChip } from '../../components/admin/StatusChip';
import { FilterBar } from '../../components/admin/FilterBar';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { Match, MatchParticipant, User, Court, Tournament } from '../../types';
import {
  formatMatchDateTime,
  formatMatchType,
  getMatchParticipants,
  matchStatusEquals,
  participantDisplayName,
  registeredParticipants,
  statusFilterToApi,
} from '../../utils/matchDisplay';
import {
  EMPTY_SCORE_SETS,
  applySetChange,
  buildScoreSubmitPayload,
  countSetsWon,
  formatMatchScoreDisplay,
  setsFromStored,
  syncSetRows,
} from '../../utils/scoreForm';

export const AdminMatchesPage: React.FC = () => {
  const { matches, loading, error, fetchMatches, addMatch, updateMatch, deleteMatch } = useMatchStore();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const viewMatchId = (location.state as { viewMatchId?: number })?.viewMatchId;
  const [courts, setCourts] = useState<Court[]>([]);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [openRemovePickDialog, setOpenRemovePickDialog] = useState(false);
  const [removeCandidates, setRemoveCandidates] = useState<MatchParticipant[]>([]);
  const [openScoreDialog, setOpenScoreDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterTournament, setFilterTournament] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [datePreset, setDatePreset] = useState<string>('week');
  const [searchQuery, setSearchQuery] = useState('');

  const getDateRangeParams = useCallback((preset: string) => {
    const now = new Date();
    const start = (d: Date) => {
      const x = new Date(d);
      x.setHours(0, 0, 0, 0);
      return x.toISOString();
    };
    const end = (d: Date) => {
      const x = new Date(d);
      x.setHours(23, 59, 59, 999);
      return x.toISOString();
    };
    if (preset === 'today') {
      return { date_from: start(now), date_to: end(now) };
    }
    if (preset === 'week') {
      const from = new Date(now);
      from.setDate(from.getDate() - 7);
      const to = new Date(now);
      to.setDate(to.getDate() + 14);
      return { date_from: start(from), date_to: end(to) };
    }
    if (preset === 'month') {
      const from = new Date(now);
      from.setDate(from.getDate() - 30);
      const to = new Date(now);
      to.setDate(to.getDate() + 60);
      return { date_from: start(from), date_to: end(to) };
    }
    if (preset === 'upcoming') {
      const to = new Date(now);
      to.setDate(to.getDate() + 90);
      return { date_from: start(now), date_to: end(to) };
    }
    return {};
  }, []);

  const loadMatches = useCallback(async () => {
    const range = getDateRangeParams(datePreset);
    await fetchMatches({
      limit: 100,
      ...range,
      ...(filterStatus !== 'all' ? { status: statusFilterToApi(filterStatus) } : {}),
      ...(filterTournament !== 'all' ? { tournament_id: parseInt(filterTournament, 10) } : {}),
      ...(filterType === 'tournament' ? { is_tournament_match: true } : {}),
      ...(filterType === 'regular' ? { is_tournament_match: false } : {}),
    });
  }, [datePreset, filterStatus, filterTournament, filterType, fetchMatches, getDateRangeParams]);
  const [formData, setFormData] = useState({
    court_id: '',
    time_slot_id: '',
    date: '',
    time: '',
    max_players: 4,
    required_skill_min: 1,
    required_skill_max: 10,
    tournament_id: '',
  });
  const [availableTimeSlots, setAvailableTimeSlots] = useState<any[]>([]);
  const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [assignData, setAssignData] = useState({
    user_id: '',
    team_number: 1,
    position: 'left',
  });
  const [scoreData, setScoreData] = useState({
    sets: [...EMPTY_SCORE_SETS],
    team1_score: 0,
    team2_score: 0,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (viewMatchId && matches.length > 0 && !loading) {
      const match = matches.find((m) => m.id === viewMatchId);
      if (match) {
        setSelectedMatch(match);
        setOpenViewDialog(true);
      }
      navigate('/admin/matches', { replace: true, state: {} });
    }
  }, [viewMatchId, matches, loading, navigate]);

  const loadData = async () => {
    await loadMatches();
    loadCourts();
    loadTournaments();
    loadUsers();
  };

  useEffect(() => {
    if (user?.is_admin) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, datePreset, filterStatus, filterTournament, filterType]);

  const loadCourts = async () => {
    try {
      const response = await apiService.getAllCourts();
      // Ensure response.data is always an array
      const data: any = response?.data;
      const courtsArray = Array.isArray(data) ? data : (Array.isArray(data?.courts) ? data.courts : (Array.isArray(data?.data) ? data.data : []));
      setCourts(courtsArray.filter((court: Court) => court.is_active));
    } catch (error) {
      console.error('Failed to load courts:', error);
      setCourts([]); // Always set empty array on error
    }
  };

  const loadTournaments = async () => {
    try {
      const response = await apiService.getAllTournaments();
      // Ensure response.data is always an array
      const data: any = response?.data;
      const tournamentsArray = Array.isArray(data) ? data : (Array.isArray(data?.tournaments) ? data.tournaments : (Array.isArray(data?.data) ? data.data : []));
      setTournaments(tournamentsArray);
    } catch (error) {
      console.error('Failed to load tournaments:', error);
      setTournaments([]); // Always set empty array on error
    }
  };

  const loadUsers = async () => {
    try {
      const response = await apiService.getAllUsers();
      // Ensure response.data is always an array
      const data: any = response?.data;
      const usersArray = Array.isArray(data) ? data : (Array.isArray(data?.users) ? data.users : (Array.isArray(data?.data) ? data.data : []));
      setUsers(usersArray);
    } catch (error) {
      console.error('Failed to load users:', error);
      setUsers([]); // Always set empty array on error
    }
  };

  const handleCreate = () => {
    setSelectedMatch(null);
    setFormData({
      court_id: '',
      time_slot_id: '',
      date: '',
      time: '',
      max_players: 4,
      required_skill_min: 1,
      required_skill_max: 10,
      tournament_id: '',
    });
    setSelectedDate('');
    setAvailableTimeSlots([]);
    setErrors({});
    setOpenEditDialog(true);
  };

  const handleEdit = (match: Match) => {
    setSelectedMatch(match);
    const matchDate = match.date ? match.date.split('T')[0] : '';
    const matchTime = match.time || '';
    setFormData({
      court_id: match.court_id?.toString() || '',
      time_slot_id: '',
      date: matchDate,
      time: matchTime,
      max_players: match.max_players || 4,
      required_skill_min: match.required_skill_min || 1,
      required_skill_max: match.required_skill_max || 10,
      tournament_id: match.tournament_id?.toString() || '',
    });
    setSelectedDate('');
    setAvailableTimeSlots([]);
    setErrors({});
    setOpenEditDialog(true);
  };

  const loadTimeSlots = async (courtId: string, date: string) => {
    if (!courtId || !date) {
      setAvailableTimeSlots([]);
      return;
    }

    try {
      setLoadingTimeSlots(true);
      const response = await apiService.getTimeSlots({
        court_id: parseInt(courtId),
        date_from: date,
        date_to: date,
        include_booked: false,
      });
      
      console.log('Time slots response:', response);
      // Handle both array and object with data property
      const data = response.data as any;
      const timeSlotsData = Array.isArray(data) 
        ? data 
        : (data?.data || data || []);
      
      // Filter available slots (not booked)
      const available = timeSlotsData.filter((slot: any) => 
        slot.status === 'available' || slot.status === 'AVAILABLE' || slot.status === 'open' || !slot.is_booked
      );
      console.log('Available time slots:', available);
      setAvailableTimeSlots(available);
    } catch (error: any) {
      console.error('Failed to load time slots:', error);
      console.error('Error details:', error.response?.data);
      setAvailableTimeSlots([]);
    } finally {
      setLoadingTimeSlots(false);
    }
  };

  const handleSave = async () => {
    const newErrors: Record<string, string> = {};
    if (!formData.court_id) newErrors.court_id = 'Court is required';
    
    // For new matches, require time slot; for editing, allow date/time
    if (!selectedMatch) {
      if (!formData.time_slot_id) {
        newErrors.time_slot_id = 'Time slot is required';
      }
    } else {
      if (!formData.date) newErrors.date = 'Date is required';
      if (!formData.time) newErrors.time = 'Time is required';
    }
    
    if (formData.max_players < 2 || formData.max_players > 8) {
      newErrors.max_players = 'Max players must be between 2 and 8';
    }
    if (formData.required_skill_min > formData.required_skill_max) {
      newErrors.required_skill_max = 'Max skill must be >= min skill';
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      let matchData: any;
      
      if (selectedMatch) {
        // For editing, use form data
        matchData = {
          court_id: parseInt(formData.court_id),
          date: `${formData.date}T${formData.time}:00`,
          time: formData.time,
          max_players: formData.max_players,
          required_skill_min: formData.required_skill_min,
          required_skill_max: formData.required_skill_max,
        };
        if (formData.tournament_id) {
          matchData.tournament_id = parseInt(formData.tournament_id);
        }
        await updateMatch(selectedMatch.id, matchData);
      } else {
        // For creating, use time slot data
        const selectedSlot = availableTimeSlots.find(slot => slot.id === parseInt(formData.time_slot_id));
        if (!selectedSlot) {
          setErrors({ time_slot_id: 'Please select a valid time slot' });
          return;
        }

        // Format date and time for backend (datetime format)
        const slotDate = new Date(selectedSlot.date);
        const [hours, minutes] = selectedSlot.start_time.split(':');
        slotDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        
        matchData = {
          court_id: selectedSlot.court_id,
          date: slotDate.toISOString(),
          time: selectedSlot.start_time,
          max_players: formData.max_players,
          required_skill_min: formData.required_skill_min,
          required_skill_max: formData.required_skill_max,
        };
        if (formData.tournament_id) {
          matchData.tournament_id = parseInt(formData.tournament_id);
        }
        await addMatch(matchData);
      }
      
      setOpenEditDialog(false);
      loadData();
    } catch (err: any) {
      console.error('Error saving match:', err);
      let errorMessage = 'Failed to save match';
      
      if (err.response?.data) {
        const errorData = err.response.data;
        
        // Handle Pydantic validation errors (422)
        if (errorData.detail && Array.isArray(errorData.detail)) {
          const validationErrors = errorData.detail.map((e: any) => {
            const field = e.loc?.join('.') || 'field';
            return `${field}: ${e.msg}`;
          }).join(', ');
          errorMessage = `Validation errors: ${validationErrors}`;
        } else if (errorData.detail) {
          errorMessage = typeof errorData.detail === 'string' 
            ? errorData.detail 
            : JSON.stringify(errorData.detail);
        }
      }
      
      setErrors({ submit: errorMessage });
    }
  };

  const handleDelete = (match: Match) => {
    setSelectedMatch(match);
    setConfirmMessage(`Are you sure you want to delete match #${match.id}?`);
    setConfirmAction(() => async () => {
      try {
        await deleteMatch(match.id);
        loadData();
      } catch (err: any) {
        console.error('Failed to delete match:', err);
      }
    });
    setOpenConfirmDialog(true);
  };

  const handleAssignPlayer = (match: Match) => {
    setSelectedMatch(match);
    setAssignData({ user_id: '', team_number: 1, position: 'left' });
    setOpenAssignDialog(true);
  };

  const handleSaveAssign = async () => {
    if (!selectedMatch || !assignData.user_id) return;
    try {
      await apiService.assignPlayersToMatch(selectedMatch.id, [{
        user_id: parseInt(assignData.user_id),
        team_number: assignData.team_number,
        position: assignData.position,
      }]);
      setOpenAssignDialog(false);
      if (openViewDialog) {
        await refreshSelectedMatch();
      } else {
        loadData();
      }
    } catch (err: any) {
      setErrors({ assign: err.response?.data?.detail || 'Failed to assign player' });
    }
  };

  const handleRemovePlayer = (match: Match, userId: number) => {
    setSelectedMatch(match);
    const participant = getMatchParticipants(match).find((p) => p.user_id === userId);
    setConfirmMessage(
      `Remove ${participantDisplayName(participant || { id: 0, team_number: 0, position: '', status: '', joined_at: '' })} from match #${match.id}?`
    );
    setConfirmAction(() => async () => {
      try {
        await apiService.removePlayerFromMatch(match.id, userId);
        setOpenRemovePickDialog(false);
        if (openViewDialog) {
          const response = await apiService.getMatch(match.id);
          setSelectedMatch(response.data);
        }
        loadData();
      } catch (err: any) {
        console.error('Failed to remove player:', err);
      }
    });
    setOpenConfirmDialog(true);
  };

  const handleRemovePlayerClick = (match: Match) => {
    const withUser = registeredParticipants(match);
    if (withUser.length === 0) return;
    if (withUser.length === 1 && withUser[0].user_id != null) {
      handleRemovePlayer(match, withUser[0].user_id);
      return;
    }
    setSelectedMatch(match);
    setRemoveCandidates(withUser);
    setOpenRemovePickDialog(true);
  };

  const handleCompleteMatch = (match: Match) => {
    setSelectedMatch(match);
    setConfirmMessage(`Mark match #${match.id} as completed?`);
    setConfirmAction(() => async () => {
      try {
        await apiService.completeMatch(match.id, {});
        if (openViewDialog && selectedMatch?.id === match.id) {
          await refreshSelectedMatch();
        } else {
          loadData();
        }
      } catch (err: any) {
        console.error('Failed to complete match:', err);
      }
    });
    setOpenConfirmDialog(true);
  };

  const handleApproveMatch = async (match: Match) => {
    try {
      await apiService.approveTournamentMatch(match.id);
      if (openViewDialog && selectedMatch?.id === match.id) {
        await refreshSelectedMatch();
      } else {
        loadData();
      }
    } catch (err: any) {
      console.error('Failed to approve match:', err);
    }
  };

  const handleRejectMatch = (match: Match) => {
    setSelectedMatch(match);
    setConfirmMessage(`Reject match #${match.id}?`);
    setConfirmAction(() => async () => {
      try {
        await apiService.rejectTournamentMatch(match.id, 'Rejected by admin');
        if (openViewDialog && selectedMatch?.id === match.id) {
          await refreshSelectedMatch();
        } else {
          loadData();
        }
      } catch (err: any) {
        console.error('Failed to reject match:', err);
      }
    });
    setOpenConfirmDialog(true);
  };

  const handleSubmitScore = (match: Match) => {
    setSelectedMatch(match);
    const existing = match.scores?.[0];
    const loadedSets = existing?.sets?.length
      ? setsFromStored(existing.sets as { team1: number; team2: number }[])
      : [...EMPTY_SCORE_SETS];
    const setsWon = countSetsWon(loadedSets);
    setScoreData({
      sets: loadedSets,
      team1_score: setsWon.team1,
      team2_score: setsWon.team2,
    });
    setErrors({});
    setOpenScoreDialog(true);
  };

  const handleSaveScore = async () => {
    if (!selectedMatch) return;
    try {
      const payload = buildScoreSubmitPayload(scoreData.sets);
      await apiService.adminSubmitScore(selectedMatch.id, payload);
      setOpenScoreDialog(false);
      if (openViewDialog) {
        await refreshSelectedMatch();
      } else {
        loadData();
      }
    } catch (err: any) {
      setErrors({ score: err.message || err.response?.data?.detail || 'Failed to submit score' });
    }
  };

  const handleViewMatch = async (match: Match) => {
    try {
      const response = await apiService.getMatch(match.id);
      setSelectedMatch(response.data);
      setOpenViewDialog(true);
    } catch (err: any) {
      console.error('Failed to load match details:', err);
    }
  };

  const refreshSelectedMatch = useCallback(async () => {
    if (!selectedMatch) return;
    try {
      const response = await apiService.getMatch(selectedMatch.id);
      setSelectedMatch(response.data);
      await loadData();
    } catch (err: any) {
      console.error('Failed to refresh match:', err);
    }
  }, [selectedMatch, loadData]);

  const filteredMatches = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return matches
      .filter((match) => {
        if (filterStatus !== 'all' && !matchStatusEquals(match.status, filterStatus)) {
          return false;
        }
        if (filterTournament !== 'all' && match.tournament_id !== parseInt(filterTournament, 10)) {
          return false;
        }
        if (filterType === 'tournament' && !match.is_tournament_match) return false;
        if (filterType === 'regular' && match.is_tournament_match) return false;
        if (!q) return true;
        return (
          match.id.toString().includes(q) ||
          match.court_name?.toLowerCase().includes(q) ||
          match.club_name?.toLowerCase().includes(q) ||
          match.tournament_name?.toLowerCase().includes(q) ||
          match.creator_name?.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => {
        const ta = a.date ? new Date(a.date).getTime() : 0;
        const tb = b.date ? new Date(b.date).getTime() : 0;
        return tb - ta;
      });
  }, [matches, searchQuery, filterStatus, filterTournament, filterType]);

  const columns: GridColDef[] = [
    { field: 'id', headerName: '#', width: 56 },
    {
      field: 'when',
      headerName: 'When',
      width: 108,
      valueGetter: (_v, row) => {
        const d = row.date ? new Date(row.date) : null;
        const day = d && !Number.isNaN(d.getTime())
          ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
          : '—';
        const time = row.time?.slice(0, 5) || '';
        return `${day} ${time}`.trim();
      },
    },
    {
      field: 'court_name',
      headerName: 'Court / Club',
      flex: 1,
      minWidth: 140,
      valueGetter: (_v, row) => {
        const court = row.court_name || `Court ${row.court_id}`;
        return row.club_name ? `${court} · ${row.club_name}` : court;
      },
    },
    {
      field: 'tournament_name',
      headerName: 'Event',
      width: 130,
      valueGetter: (_v, row) =>
        row.is_tournament_match ? row.tournament_name || `T#${row.tournament_id}` : 'Regular',
    },
    {
      field: 'participants',
      headerName: 'Pl.',
      width: 52,
      valueGetter: (_v, row) => `${row.participants_count || 0}/${row.max_players || 4}`,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      valueGetter: (_v, row) => row.status,
      renderCell: (params) => <StatusChip status={String(params.value ?? '')} />,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      width: 56,
      getActions: (params) => {
        const match = params.row as Match;
        const actions = [];

        actions.push(
          <GridActionsCellItem
            icon={<Visibility />}
            label="View"
            onClick={() => handleViewMatch(match)}
          />
        );

        const terminal =
          matchStatusEquals(match.status, 'completed') ||
          matchStatusEquals(match.status, 'rejected') ||
          matchStatusEquals(match.status, 'cancelled');

        if (!terminal) {
          actions.push(
            <GridActionsCellItem
              icon={<Edit />}
              label="Edit"
              onClick={() => handleEdit(match)}
            />
          );

          if (
            matchStatusEquals(match.status, 'open') ||
            matchStatusEquals(match.status, 'confirmed')
          ) {
            actions.push(
              <GridActionsCellItem
                icon={<PersonAdd />}
                label="Assign Player"
                onClick={() => handleAssignPlayer(match)}
              />
            );
          }

          if (registeredParticipants(match).length > 0 && matchStatusEquals(match.status, 'open')) {
            actions.push(
              <GridActionsCellItem
                icon={<PersonRemove />}
                label="Remove Player"
                onClick={() => handleRemovePlayerClick(match)}
              />
            );
          }

          if (matchStatusEquals(match.status, 'waiting_admin_confirmation')) {
            actions.push(
              <GridActionsCellItem
                icon={<CheckCircle />}
                label="Approve"
                onClick={() => handleApproveMatch(match)}
                showInMenu
              />
            );
            actions.push(
              <GridActionsCellItem
                icon={<Cancel />}
                label="Reject"
                onClick={() => handleRejectMatch(match)}
                showInMenu
              />
            );
          }

          if (matchStatusEquals(match.status, 'confirmed')) {
            actions.push(
              <GridActionsCellItem
                icon={<CheckCircle />}
                label="Complete"
                onClick={() => handleCompleteMatch(match)}
                showInMenu
              />
            );
          }
        }

        if (matchStatusEquals(match.status, 'completed')) {
          actions.push(
            <GridActionsCellItem
              icon={<SportsScore />}
              label="Submit Score"
              onClick={() => handleSubmitScore(match)}
              showInMenu
            />
          );
        }

        actions.push(
          <GridActionsCellItem
            icon={<Delete />}
            label="Delete"
            onClick={() => handleDelete(match)}
            showInMenu
          />
        );

        return actions;
      },
    },
  ];

  if (!user?.is_admin) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          <Typography variant="h6">Access Denied</Typography>
          <Typography>You need admin privileges to access this section.</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" component="h1">
          Match Management
        </Typography>
        <Button variant="contained" size="small" startIcon={<Add />} onClick={handleCreate}>
          Create
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <FilterBar
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        filters={[
          {
            label: 'Date',
            value: datePreset,
            options: [
              { value: 'today', label: 'Today' },
              { value: 'week', label: '±7 days' },
              { value: 'month', label: '±30 days' },
              { value: 'upcoming', label: 'Upcoming' },
              { value: 'all', label: 'Any date' },
            ],
            onChange: setDatePreset,
          },
          {
            label: 'Status',
            value: filterStatus,
            options: [
              { value: 'all', label: 'All' },
              { value: 'open', label: 'Open' },
              { value: 'waiting_admin_confirmation', label: 'Pending' },
              { value: 'confirmed', label: 'Confirmed' },
              { value: 'completed', label: 'Done' },
              { value: 'rejected', label: 'Rejected' },
            ],
            onChange: setFilterStatus,
          },
          {
            label: 'Type',
            value: filterType,
            options: [
              { value: 'all', label: 'All types' },
              { value: 'tournament', label: 'Tournament' },
              { value: 'regular', label: 'Regular' },
            ],
            onChange: setFilterType,
          },
          {
            label: 'Tournament',
            value: filterTournament,
            options: [
              { value: 'all', label: 'All tournaments' },
              ...tournaments.map((t) => ({ value: String(t.id), label: t.name })),
            ],
            onChange: setFilterTournament,
          },
        ]}
        onClear={() => {
          setSearchQuery('');
          setFilterStatus('all');
          setFilterTournament('all');
          setFilterType('all');
          setDatePreset('week');
        }}
      />

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {filteredMatches.length} match{filteredMatches.length === 1 ? '' : 'es'} — tap a row to view details
      </Typography>

      <Card>
        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
          <DataTable
            rows={filteredMatches}
            columns={columns}
            loading={loading}
            pageSize={50}
            rowsPerPageOptions={[25, 50, 100]}
            onRowClick={(params) => handleViewMatch(params.row as Match)}
            disableRowSelectionOnClick={false}
          />
        </CardContent>
      </Card>

      {/* Edit/Create Dialog */}
      <FormDialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        title={selectedMatch ? 'Edit Match' : 'Create Match'}
        onSubmit={handleSave}
        loading={false}
      >
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
              <FormControl fullWidth error={!!errors.court_id}>
                <InputLabel>Court</InputLabel>
                <Select
                  value={formData.court_id}
                  label="Court"
                  onChange={(e) => {
                    setFormData({ ...formData, court_id: e.target.value, time_slot_id: '' });
                    if (!selectedMatch && selectedDate) {
                      loadTimeSlots(e.target.value, selectedDate);
                    }
                  }}
                >
                  {courts.map((court) => (
                    <MenuItem key={court.id} value={court.id}>
                      {court.name} {(court as any).club_name ? `- ${(court as any).club_name}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
              <FormControl fullWidth>
                <InputLabel>Tournament (Optional)</InputLabel>
                <Select
                  value={formData.tournament_id}
                  label="Tournament (Optional)"
                  onChange={(e) => setFormData({ ...formData, tournament_id: e.target.value })}
                >
                  <MenuItem value="">No Tournament</MenuItem>
                  {tournaments.map((tournament) => (
                    <MenuItem key={tournament.id} value={tournament.id}>
                      {tournament.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          {selectedMatch ? (
            // For editing, show date/time inputs
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                <TextField
                  label="Date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  error={!!errors.date}
                  helperText={errors.date}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                <TextField
                  label="Time"
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  error={!!errors.time}
                  helperText={errors.time}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Box>
          ) : (
            // For creating, show time slot selection
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                  <TextField
                    label="Select Date"
                    type="date"
                    value={selectedDate}
                    onChange={(e) => {
                      setSelectedDate(e.target.value);
                      if (formData.court_id) {
                        loadTimeSlots(formData.court_id, e.target.value);
                      }
                    }}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                  <FormControl fullWidth error={!!errors.time_slot_id}>
                    <InputLabel>Time Slot</InputLabel>
                    <Select
                      value={formData.time_slot_id}
                      label="Time Slot"
                      onChange={(e) => setFormData({ ...formData, time_slot_id: e.target.value })}
                      disabled={!formData.court_id || !selectedDate || loadingTimeSlots}
                    >
                      {loadingTimeSlots ? (
                        <MenuItem disabled>Loading time slots...</MenuItem>
                      ) : availableTimeSlots.length === 0 ? (
                        <MenuItem disabled>
                          {!formData.court_id ? 'Select a court first' : !selectedDate ? 'Select a date first' : 'No available time slots'}
                        </MenuItem>
                      ) : (
                        availableTimeSlots.map((slot) => (
                          <MenuItem key={slot.id} value={slot.id}>
                            {slot.start_time} - {slot.end_time} {slot.price ? `($${slot.price})` : ''}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {errors.time_slot_id && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                        {errors.time_slot_id}
                      </Typography>
                    )}
                  </FormControl>
                </Box>
              </Box>
              {formData.court_id && selectedDate && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => loadTimeSlots(formData.court_id, selectedDate)}
                  disabled={loadingTimeSlots}
                >
                  {loadingTimeSlots ? 'Loading...' : 'Refresh Time Slots'}
                </Button>
              )}
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <TextField
                label="Max Players"
                type="number"
                value={formData.max_players}
                onChange={(e) => setFormData({ ...formData, max_players: parseInt(e.target.value) })}
                error={!!errors.max_players}
                helperText={errors.max_players}
                fullWidth
                inputProps={{ min: 2, max: 8 }}
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <TextField
                label="Min Skill Level"
                type="number"
                value={formData.required_skill_min}
                onChange={(e) => setFormData({ ...formData, required_skill_min: parseInt(e.target.value) })}
                fullWidth
                inputProps={{ min: 1, max: 10 }}
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <TextField
                label="Max Skill Level"
                type="number"
                value={formData.required_skill_max}
                onChange={(e) => setFormData({ ...formData, required_skill_max: parseInt(e.target.value) })}
                error={!!errors.required_skill_max}
                helperText={errors.required_skill_max}
                fullWidth
                inputProps={{ min: 1, max: 10 }}
              />
            </Box>
          </Box>
        </Stack>
        {errors.submit && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errors.submit}
          </Alert>
        )}
      </FormDialog>

      {/* Assign Player Dialog */}
      <Dialog open={openAssignDialog} onClose={() => setOpenAssignDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Player to Match</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Player</InputLabel>
              <Select
                value={assignData.user_id}
                label="Player"
                onChange={(e) => setAssignData({ ...assignData, user_id: e.target.value })}
              >
                {(Array.isArray(users) ? users : []).map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name}
                    {user.elo_rating != null ? ` (ELO: ${Math.round(user.elo_rating)})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Team</InputLabel>
                <Select
                  value={assignData.team_number}
                  label="Team"
                  onChange={(e) => setAssignData({ ...assignData, team_number: e.target.value as number })}
                >
                  <MenuItem value={1}>Team 1</MenuItem>
                  <MenuItem value={2}>Team 2</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Position</InputLabel>
                <Select
                  value={assignData.position}
                  label="Position"
                  onChange={(e) => setAssignData({ ...assignData, position: e.target.value })}
                >
                  <MenuItem value="left">Left</MenuItem>
                  <MenuItem value="right">Right</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Stack>
          {errors.assign && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errors.assign}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAssignDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveAssign} variant="contained" disabled={!assignData.user_id}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submit Score Dialog */}
      <Dialog open={openScoreDialog} onClose={() => setOpenScoreDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Submit Score for Match #{selectedMatch?.id}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Set Scores
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Enter game scores per set (e.g. 6-4, 6-2). Set 3 appears only if the match is tied 1-1.
            </Typography>
            {syncSetRows(scoreData.sets).map((set, index) => (
              <Box key={index}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Set {index + 1}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    label="Team 1"
                    type="number"
                    value={set.team1}
                    onChange={(e) => {
                      const nextSets = applySetChange(
                        scoreData.sets,
                        index,
                        'team1',
                        parseInt(e.target.value, 10) || 0
                      );
                      setScoreData({
                        sets: nextSets,
                        team1_score: countSetsWon(nextSets).team1,
                        team2_score: countSetsWon(nextSets).team2,
                      });
                    }}
                    sx={{ flex: 1 }}
                    inputProps={{ min: 0, max: 10 }}
                  />
                  <Typography variant="h6">-</Typography>
                  <TextField
                    label="Team 2"
                    type="number"
                    value={set.team2}
                    onChange={(e) => {
                      const nextSets = applySetChange(
                        scoreData.sets,
                        index,
                        'team2',
                        parseInt(e.target.value, 10) || 0
                      );
                      setScoreData({
                        sets: nextSets,
                        team1_score: countSetsWon(nextSets).team1,
                        team2_score: countSetsWon(nextSets).team2,
                      });
                    }}
                    sx={{ flex: 1 }}
                    inputProps={{ min: 0, max: 10 }}
                  />
                </Box>
              </Box>
            ))}
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Sets won: {countSetsWon(scoreData.sets).team1} – {countSetsWon(scoreData.sets).team2}
            </Typography>
          </Stack>
          {errors.score && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errors.score}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenScoreDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveScore} variant="contained">
            Submit Score
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove player picker */}
      <Dialog open={openRemovePickDialog} onClose={() => setOpenRemovePickDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove player</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {removeCandidates.map((p) => (
              <Button
                key={p.id}
                variant="outlined"
                fullWidth
                sx={{ justifyContent: 'flex-start' }}
                onClick={() => {
                  if (selectedMatch && p.user_id != null) {
                    setOpenRemovePickDialog(false);
                    handleRemovePlayer(selectedMatch, p.user_id);
                  }
                }}
              >
                {participantDisplayName(p)} — Team {p.team_number} ({p.position})
              </Button>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRemovePickDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* View Match Dialog */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Match Details #{selectedMatch?.id}</DialogTitle>
        <DialogContent>
          {selectedMatch && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    When
                  </Typography>
                  <Typography variant="body1">
                    {formatMatchDateTime(selectedMatch.date, selectedMatch.time)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <StatusChip status={selectedMatch.status} />
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Type
                  </Typography>
                  <Typography variant="body1">{formatMatchType(selectedMatch.match_type)}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Court / Club
                  </Typography>
                  <Typography variant="body1">
                    {selectedMatch.court_name || `Court ${selectedMatch.court_id}`}
                    {selectedMatch.club_name ? ` · ${selectedMatch.club_name}` : ''}
                  </Typography>
                </Box>
                {selectedMatch.is_tournament_match && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Tournament
                    </Typography>
                    <Typography variant="body1">
                      {selectedMatch.tournament_name || `#${selectedMatch.tournament_id}`}
                    </Typography>
                  </Box>
                )}
                {selectedMatch.creator_name && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Created by
                    </Typography>
                    <Typography variant="body1">{selectedMatch.creator_name}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Players
                  </Typography>
                  <Typography variant="body1">
                    {selectedMatch.participants_count || 0}/{selectedMatch.max_players || 4}
                    {(selectedMatch.required_skill_min != null || selectedMatch.required_skill_max != null) &&
                      ` · ELO ${selectedMatch.required_skill_min ?? '—'}–${selectedMatch.required_skill_max ?? '—'}`}
                  </Typography>
                </Box>
              </Box>
              {selectedMatch.rejection_reason && (
                <Alert severity="warning">{selectedMatch.rejection_reason}</Alert>
              )}
              {getMatchParticipants(selectedMatch).length > 0 && (
                <>
                  <Divider />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Participants
                  </Typography>
                  {getMatchParticipants(selectedMatch).map((participant) => (
                    <Box
                      key={participant.id}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      gap={1}
                    >
                      <Box>
                        <Typography variant="body1">{participantDisplayName(participant)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Team {participant.team_number} · {participant.position}
                          {!participant.user_id && ' · Guest'}
                        </Typography>
                      </Box>
                      {participant.user_id != null &&
                        matchStatusEquals(selectedMatch.status, 'open') && (
                          <Tooltip title="Remove registered player">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemovePlayer(selectedMatch, participant.user_id!)}
                            >
                              <PersonRemove />
                            </IconButton>
                          </Tooltip>
                        )}
                    </Box>
                  ))}
                </>
              )}
              {selectedMatch.scores && selectedMatch.scores.length > 0 && (
                <>
                  <Divider />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Scores
                  </Typography>
                  {selectedMatch.scores.map((score) => (
                    <Typography key={score.id} variant="body2">
                      {formatMatchScoreDisplay(score) ?? '—'}
                      {score.winner_team != null ? ` (winner: team ${score.winner_team})` : ''}
                      {(score.is_confirmed ?? score.confirmed) ? ' ✓' : ''}
                    </Typography>
                  ))}
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, pb: 2, justifyContent: 'flex-start' }}>
          {selectedMatch && (() => {
            const m = selectedMatch;
            const terminal =
              matchStatusEquals(m.status, 'completed') ||
              matchStatusEquals(m.status, 'rejected') ||
              matchStatusEquals(m.status, 'cancelled');
            const reg = registeredParticipants(m);
            return (
              <>
                {!terminal && (
                  <Button
                    size="small"
                    startIcon={<Edit />}
                    onClick={() => {
                      setOpenViewDialog(false);
                      handleEdit(m);
                    }}
                  >
                    Edit
                  </Button>
                )}
                {(matchStatusEquals(m.status, 'open') || matchStatusEquals(m.status, 'confirmed')) && (
                  <Button size="small" startIcon={<PersonAdd />} onClick={() => handleAssignPlayer(m)}>
                    Assign
                  </Button>
                )}
                {reg.length > 0 && matchStatusEquals(m.status, 'open') && (
                  <Button
                    size="small"
                    color="warning"
                    startIcon={<PersonRemove />}
                    onClick={() => handleRemovePlayerClick(m)}
                  >
                    Remove
                  </Button>
                )}
                {matchStatusEquals(m.status, 'waiting_admin_confirmation') && (
                  <>
                    <Button
                      size="small"
                      color="success"
                      startIcon={<CheckCircle />}
                      onClick={() => handleApproveMatch(m)}
                    >
                      Approve
                    </Button>
                    <Button size="small" color="error" startIcon={<Cancel />} onClick={() => handleRejectMatch(m)}>
                      Reject
                    </Button>
                  </>
                )}
                {matchStatusEquals(m.status, 'confirmed') && (
                  <Button
                    size="small"
                    color="success"
                    startIcon={<CheckCircle />}
                    onClick={() => handleCompleteMatch(m)}
                  >
                    Complete
                  </Button>
                )}
                {matchStatusEquals(m.status, 'completed') && (
                  <Button size="small" startIcon={<SportsScore />} onClick={() => handleSubmitScore(m)}>
                    {m.scores && m.scores.length > 0 ? 'Edit score' : 'Add score'}
                  </Button>
                )}
                <Button
                  size="small"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => {
                    setOpenViewDialog(false);
                    handleDelete(m);
                  }}
                >
                  Delete
                </Button>
              </>
            );
          })()}
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        title="Confirm Action"
        message={confirmMessage}
        onConfirm={() => {
          if (confirmAction) {
            confirmAction();
          }
          setOpenConfirmDialog(false);
        }}
      />
    </Box>
  );
};
